import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * testimonials — Shrine "Testimonials" at CONFIG-BY-CONFIG parity. A heading (+ the shared word-HIGHLIGHT
 * engine and an optional pattern background) over a responsive grid/slider of testimonial cards. Each
 * TESTIMONIAL is an inline child block (LEGACY model, like image-with-text's `feature` block): image OR
 * video thumbnail, star rating, quote/title/body, author + role + avatar.
 *
 * SHRINE PARITY (73 settings): every General/Cards-style/Slider/Desktop/Mobile knob is implemented here and
 * wired in Component.tsx. The Shopify-platform-only knobs are DEFERRED with justification (see the bottom of
 * this file and Component.tsx): `display_id` (section-merge UI helper), the five `color_scheme` selects map
 * onto our 3-way `colorScheme`/slider-scheme tokens, the `custom_image_background` pair (Shopify img_url
 * pipeline), reveal/scroll animations, and the live Splide carousel JS (autoplay/loop need a client island —
 * the controls + native scroll-snap render). The `line-1/2/3` heading decorations use CSS text-decoration
 * rather than Shrine's SVG glyph assets. A `role` field is ADDED to the testimonial block.
 */
export const testimonialsSchema = {
    type: 'testimonials',
    name: 'Testimonials',
    version: 2,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General (Shrine HEADER: General) ─────────────────────────────────────
        /**
         * Feature 0066. Default MANUAL and it stays manual: a live shop that typed three testimonials must not
         * start showing something else because an app was installed. The seller opts in, once, here.
         */
        {
            id: 'source',
            type: 'select',
            label: 'Where the cards come from',
            group: 'General',
            default: 'manual',
            options: [
                { value: 'manual', label: 'The cards I write' },
                { value: 'app', label: 'My real reviews' },
            ],
            info: 'With “My real reviews”, the cards are the published reviews of this product. The section hides itself when there are none.',
        },
        {
            id: 'reviewsToShow',
            type: 'range',
            label: 'Reviews to show',
            group: 'General',
            default: 6,
            min: 1,
            max: 24,
            step: 1,
            visibleWhen: { field: 'source', equals: 'app' },
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'General',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'Always display' },
                { value: 'desktop-hidden', label: 'Hide on desktop' },
                { value: 'mobile-hidden', label: 'Hide on mobile' },
            ],
        },
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: "<p>Ce qu'en disent nos clients</p>" },
            info: 'Bold a word to apply highlight 1, italic to apply highlight 2.',
        },
        // ── Heading highlight engines 1 (bold) & 2 (italic), grouped under General. ─
        ...headingHighlightFields(1, 'General').map((f) => f.id === 'headingHighlight1'
            ? { ...f, info: 'Bold a word in the heading to style it with this.' }
            : f),
        ...headingHighlightFields(2, 'General').map((f) => f.id === 'headingHighlight2'
            ? { ...f, info: 'Italicize a word in the heading to style it with this.' }
            : f),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'body',
            type: 'richtext',
            label: 'Text',
            group: 'General',
        },
        // ── Background pattern (shared engine — Shrine pattern_bg cluster). ────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'General',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'General',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'General',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'General',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Cards style (Shrine HEADER: Cards style) ─────────────────────────────
        {
            id: 'imageWidth',
            type: 'select',
            label: 'Media width',
            group: 'Cards style',
            default: 'full',
            options: [
                { value: 'third', label: 'One third' },
                { value: 'half', label: 'Half' },
                { value: 'full', label: 'Full' },
            ],
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Media ratio',
            group: 'Cards style',
            default: 'square',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'square', label: 'Square (1:1)' },
                { value: 'landscape', label: 'Landscape (16:10)' },
                { value: 'circle', label: 'Circle' },
            ],
        },
        {
            id: 'columnAlignment',
            type: 'select',
            label: 'Text alignment',
            group: 'Cards style',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'showStars',
            type: 'checkbox',
            label: 'Show star rating',
            group: 'Cards style',
            default: true,
        },
        {
            id: 'inactiveStarsStyle',
            type: 'select',
            label: 'Inactive stars style',
            group: 'Cards style',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'outlined', label: 'Outlined' },
                { value: 'hidden', label: 'Hidden' },
            ],
            visibleWhen: { field: 'showStars', equals: true },
        },
        {
            id: 'starColor',
            type: 'color',
            label: 'Stars color',
            group: 'Cards style',
            default: { ref: 'custom', value: '#ffcc00' },
            visibleWhen: { field: 'showStars', equals: true },
        },
        {
            id: 'inactiveStarsColor',
            type: 'color',
            label: 'Inactive stars color',
            group: 'Cards style',
            default: { ref: 'custom', value: '#ececec' },
            visibleWhen: { field: 'showStars', equals: true },
        },
        {
            id: 'showQuotes',
            type: 'checkbox',
            label: 'Show quote marks',
            group: 'Cards style',
            default: true,
        },
        {
            id: 'quotesScheme',
            type: 'select',
            label: 'Quote marks color',
            group: 'Cards style',
            default: 'accent-2',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
            visibleWhen: { field: 'showQuotes', equals: true },
        },
        {
            // How each testimonial card renders: 'quote' (the historical Shrine quote card — stars, body,
            // author row) or 'media-overlay' (the image/video IS the card: full-bleed media, author + role
            // overlaid bottom-left on a dark gradient, play badge bottom-right when a video link is set —
            // the "words of champions" video-testimonial anatomy). Quote-only knobs are ignored in overlay mode.
            id: 'cardStyle',
            type: 'select',
            label: 'Card style',
            group: 'Cards style',
            default: 'quote',
            options: [
                { value: 'quote', label: 'Quote card' },
                { value: 'media-overlay', label: 'Media card with name overlay' },
            ],
        },
        {
            id: 'cardsScheme',
            type: 'select',
            label: 'Cards color',
            group: 'Cards style',
            default: 'bg-overlay',
            options: [
                { value: 'bg-overlay', label: 'Background overlay' },
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'cardsCustomBackground',
            type: 'color',
            label: 'Cards background',
            group: 'Cards style',
            default: { ref: 'custom', value: '#F3F3F3' },
            visibleWhen: { field: 'cardsScheme', equals: 'custom' },
        },
        {
            id: 'cardsCustomGradient',
            type: 'gradient',
            label: 'Cards gradient',
            group: 'Cards style',
            visibleWhen: { field: 'cardsScheme', equals: 'custom' },
        },
        {
            id: 'cardsCustomText',
            type: 'color',
            label: 'Cards text',
            group: 'Cards style',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'cardsScheme', equals: 'custom' },
        },
        // ── Slider (Shrine HEADER: Slider) ───────────────────────────────────────
        {
            id: 'sliderType',
            type: 'select',
            label: 'Slider type',
            group: 'Slider',
            default: 'slide',
            info: 'Classic (Slide) or infinite (Loop). The looping/autoplay carousel needs a client island.',
            options: [
                { value: 'slide', label: 'Classic' },
                { value: 'loop', label: 'Infinite loop' },
            ],
        },
        {
            id: 'autoplay',
            type: 'checkbox',
            label: 'Autoplay',
            group: 'Slider',
            default: false,
        },
        {
            id: 'autoplaySpeed',
            type: 'range',
            label: 'Autoplay speed',
            group: 'Slider',
            min: 1,
            max: 15,
            step: 0.5,
            unit: 's',
            default: 5,
            visibleWhen: { field: 'autoplay', equals: true },
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Slider',
            default: true,
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color',
            group: 'Slider',
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Desktop (Shrine HEADER: Desktop) ─────────────────────────────────────
        {
            id: 'desktopFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Desktop',
            default: false,
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Slides per page',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: 'Desktop',
            default: false,
            info: 'When off, cards lay out as a static grid on desktop.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll per move',
            group: 'Desktop',
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: 'Desktop',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: 'Desktop',
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopPaddingCalc',
            type: 'checkbox',
            label: 'Disable empty preview on first & last slide',
            group: 'Desktop',
            default: true,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Desktop',
            default: false,
            info: 'Only applies when Slides per page is 1.',
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position (desktop)',
            group: 'Desktop',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position (desktop)',
            group: 'Desktop',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Mobile (Shrine HEADER: Mobile) ───────────────────────────────────────
        {
            id: 'sliderMobile',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: 'Mobile',
            default: true,
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slides',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileAdaptiveHeight',
            type: 'checkbox',
            label: 'Adaptive height',
            group: 'Mobile',
            default: false,
            visibleWhen: { field: 'sliderMobile', equals: true },
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position (mobile)',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position (mobile)',
            group: 'Mobile',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        // ── Section (universal: color scheme, padding_top/bottom, width) ─────────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    // The TESTIMONIAL inline block (Shrine `column`): media + rating + quote + author/role/avatar.
    blocks: [
        {
            type: 'testimonial',
            name: 'Testimonial',
            max: 12,
            fields: [
                { id: 'image', type: 'image', label: 'Image' },
                // Shrine HEADER: or — a video thumbnail testimonial. The native Shopify video object is DEFERRED
                // (Shopify CDN/sources pipeline); we expose a poster image + an external video link instead.
                { id: 'videoThumbnail', type: 'image', label: 'Video thumbnail' },
                { id: 'video', type: 'link', label: 'Video link (optional)' },
                {
                    id: 'starRating',
                    type: 'range',
                    label: 'Rating',
                    min: 1,
                    max: 5,
                    step: 1,
                    default: 5,
                },
                { id: 'title', type: 'richtext', label: 'Heading' },
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Quote',
                },
                { id: 'authorAvatar', type: 'image', label: 'Author avatar' },
                { id: 'author', type: 'text', label: 'Author', default: 'Client', maxLength: 80 },
                { id: 'role', type: 'text', label: 'Author role', maxLength: 80 },
            ],
        },
    ],
    maxBlocks: 12,
    // Lazily migrate v1 documents: v1 stored `text` as a plain string (textarea); v2 stores richtext.
    migrate(fromVersion, settings) {
        if (fromVersion >= 2)
            return settings;
        const next = { ...settings };
        if (typeof next.body === 'string') {
            next.body = { format: 'html', html: `<p>${next.body}</p>` };
        }
        return next;
    },
};
export const testimonialsPresets = [
    {
        name: 'Testimonials',
        settings: {
            visibility: 'always-display',
            heading: { format: 'html', html: '<p>Ils nous font <strong>confiance</strong></p>' },
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            body: {
                format: 'html',
                html: '<p>Des centaines de clients satisfaits partout au Maroc.</p>',
            },
            imageWidth: 'full',
            imageRatio: 'circle',
            columnsDesktop: 3,
            columnAlignment: 'center',
            showStars: true,
            inactiveStarsStyle: 'solid',
            showQuotes: true,
            quotesScheme: 'accent-2',
            cardsScheme: 'bg-overlay',
            sliderType: 'slide',
            autoplay: false,
            transparentArrows: true,
            arrowsScheme: 'inverse',
            dotsScheme: 'inverse',
            desktopFullPage: false,
            sliderDesktop: false,
            desktopSpacing: 40,
            desktopArrows: 'sides',
            desktopDots: 'under',
            sliderMobile: true,
            mobileArrows: 'under',
            mobileDots: 'under',
            // A clean LIGHT reviews band near the bottom of the page.
            colorScheme: 'light',
            paddingTop: 72,
            paddingBottom: 72,
            width: 'standard',
        },
        // No default avatar images — the default state stays clean (stars + quote + author), and the seller adds
        // a photo if they want one. Seeding demo image URLs rendered as BROKEN images in the builder preview.
        blocks: [
            {
                type: 'testimonial',
                settings: {
                    starRating: 5,
                    text: {
                        format: 'html',
                        html: '<p>Livraison rapide et qualité au rendez-vous. Je recommande les yeux fermés !</p>',
                    },
                    author: 'Sara B.',
                    role: '@t:section.verified_customer_f',
                },
            },
            {
                type: 'testimonial',
                settings: {
                    starRating: 5,
                    text: {
                        format: 'html',
                        html: '<p>Le service client est top et le produit correspond parfaitement à la description.</p>',
                    },
                    author: 'Yassine M.',
                    role: '@t:section.verified_customer_m',
                },
            },
            {
                type: 'testimonial',
                settings: {
                    starRating: 4,
                    text: {
                        format: 'html',
                        html: '<p>Très satisfaite de mon achat. Je reviendrai sans hésiter pour une prochaine commande.</p>',
                    },
                    author: 'Imane K.',
                    role: '@t:section.verified_customer_f',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const testimonialsMeta = {
    label: 'Testimonials',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map